import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { gql, useMutation } from '@apollo/client';

import {
  SaveWireDetailsPrices,
  SaveWireDetailsPricesVariables,
} from './__generated__/SaveWireDetailsPrices';
import { SettlementCard_SettlementCard_prices } from './__generated__/SettlementCard';
import {
  SetWireDetailsPricesVisible,
  SetWireDetailsPricesVisibleVariables,
} from './__generated__/SetWireDetailsPricesVisible';
import { DealRole } from '../../../../../__generated__/globalTypes';
import { Button, UiInput } from '../../../../../ui-kit';
import { GetDealGeneralInfo_deal_listing_UserCompanyListing_seller } from '../../../dealCards/gql/__generated__/GetDealGeneralInfo';
import { GET_DEAL_TIMELINE } from '../../fragments';

export type SettlementPricesProps = {
  dealId: string;
  prices: SettlementCard_SettlementCard_prices;
  role: DealRole;
  is_complete: boolean;
  seller: GetDealGeneralInfo_deal_listing_UserCompanyListing_seller;
};

const SET_WIRE_DETAILS_PRICES_VISIBLE_MUTATION = gql`
  mutation SetWireDetailsPricesVisible($input: SetWireDetailsPricesVisibleInput!) {
    setWireDetailsPricesVisible(input: $input) {
      cleanPriceCents
      dirtyPriceCents
      isVisibleToBuyer
    }
  }
`;

const SAVE_WIRE_DETAILS_PRICES_MUTATION = gql`
  mutation SaveWireDetailsPrices($input: SaveWireDetailsPricesInput!) {
    saveWireDetailsPrices(input: $input) {
      cleanPriceCents
      dirtyPriceCents
      isVisibleToBuyer
    }
  }
`;

/** Dollars string for display: empty when unset/zero so placeholder shows; otherwise 2dp. */
const centsToInputValue = (cents: number | null | undefined) =>
  cents != null && cents !== 0 ? (cents / 100).toFixed(2) : '';

/** Drop leading zeros before another digit (e.g. 0111 → 111) without breaking 0.xx decimals. */
const sanitizePriceTyping = (value: string) => value.replace(/^0+(?=\d)/, '');

const priceInputChangeHandler =
  (setter: Dispatch<SetStateAction<string>>) => (e: ChangeEvent<HTMLInputElement>) =>
    setter(sanitizePriceTyping(e.target.value));

/**
 * Empty / incomplete input → keep existing server cents, or 0 if unset (new settlement).
 * Non-empty invalid parse → null (caller should not save).
 */
function dollarsInputToCents(raw: string, serverCents: number | null | undefined): number | null {
  const t = raw.trim();
  if (t === '' || t === '.') {
    return serverCents ?? 0;
  }
  const n = parseFloat(t);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
}

const centsMatch = (a: number | null | undefined, b: number) => (a ?? 0) === b;

export const SettlementPrices = ({ prices, dealId, role, seller }: SettlementPricesProps) => {
  // State to hold price inputs as dollars (string format)
  const [cleanPrice, setCleanPrice] = useState('');
  const [dirtyPrice, setDirtyPrice] = useState('');

  /** While Apollo still has stale `prices`, avoid syncing props → inputs (prevents flash back to old value). */
  const pendingWirePricesRef = useRef<{ clean: number; dirty: number } | null>(null);
  /** Increments on each blur-save so an older mutation cannot clear a newer pending save on failure. */
  const wireSaveGenerationRef = useRef(0);

  const [setWireDetailsPricesVisible] = useMutation<
    SetWireDetailsPricesVisible,
    SetWireDetailsPricesVisibleVariables
  >(SET_WIRE_DETAILS_PRICES_VISIBLE_MUTATION);

  const [saveWireDetailsPrices] = useMutation<
    SaveWireDetailsPrices,
    SaveWireDetailsPricesVariables
  >(SAVE_WIRE_DETAILS_PRICES_MUTATION);

  const handleSendPrices = async () => {
    try {
      await setWireDetailsPricesVisible({
        variables: { input: { dealID: dealId } },
        refetchQueries: [{ query: GET_DEAL_TIMELINE, variables: { id: dealId } }],
      });
    } catch (error) {
      console.error('Error sending prices', error);
    }
  };

  // Sync from server only when cent values actually change — not on every new `prices` object reference.
  useEffect(() => {
    if (prices.isVisibleToBuyer) return;

    const pending = pendingWirePricesRef.current;
    if (pending) {
      if (
        centsMatch(prices.cleanPriceCents, pending.clean) &&
        centsMatch(prices.dirtyPriceCents, pending.dirty)
      ) {
        pendingWirePricesRef.current = null;
      } else {
        return;
      }
    }

    setCleanPrice(centsToInputValue(prices.cleanPriceCents));
    setDirtyPrice(centsToInputValue(prices.dirtyPriceCents));
  }, [prices.isVisibleToBuyer, prices.cleanPriceCents, prices.dirtyPriceCents]);

  const persistWirePricesOnBlur = useCallback(async () => {
    if (!prices || prices.isVisibleToBuyer) return;
    const cleanCents = dollarsInputToCents(cleanPrice, prices.cleanPriceCents);
    const dirtyCents = dollarsInputToCents(dirtyPrice, prices.dirtyPriceCents);
    if (cleanCents === null || dirtyCents === null) return;

    const serverClean = prices.cleanPriceCents ?? 0;
    const serverDirty = prices.dirtyPriceCents ?? 0;
    if (cleanCents === serverClean && dirtyCents === serverDirty) return;

    const saveGeneration = ++wireSaveGenerationRef.current;
    pendingWirePricesRef.current = { clean: cleanCents, dirty: dirtyCents };
    try {
      const result = await saveWireDetailsPrices({
        variables: {
          input: {
            dealID: dealId,
            cleanPriceCents: cleanCents,
            dirtyPriceCents: dirtyCents,
          },
        },
        refetchQueries: [{ query: GET_DEAL_TIMELINE, variables: { id: dealId } }],
      });
      if (wireSaveGenerationRef.current !== saveGeneration) return;

      const saved = result.data?.saveWireDetailsPrices;
      const canonicalClean =
        saved?.cleanPriceCents != null ? Number(saved.cleanPriceCents) : cleanCents;
      const canonicalDirty =
        saved?.dirtyPriceCents != null ? Number(saved.dirtyPriceCents) : dirtyCents;

      setCleanPrice(centsToInputValue(canonicalClean));
      setDirtyPrice(centsToInputValue(canonicalDirty));

      // Keep blocking prop sync until cache matches server canonical values (avoids flash to stale
      // cents). Pending is updated from the mutation response so normalization cannot deadlock.
      pendingWirePricesRef.current = { clean: canonicalClean, dirty: canonicalDirty };
    } catch {
      if (wireSaveGenerationRef.current === saveGeneration) {
        pendingWirePricesRef.current = null;
      }
    }
  }, [cleanPrice, dirtyPrice, prices, dealId, saveWireDetailsPrices]);

  return (
    <div className="mt-4 w-full ">
      {role === 'BUYER' && !prices?.isVisibleToBuyer && (
        <p className="rounded-sm bg-blue-600 p-4">
          The seller is currently preparing the necessary documentation for e-signatures and
          finalizing the loan tape for reconciliation after due diligence. You will receive a
          notification once everything is ready. Thank you for your patience.
        </p>
      )}
      {prices.isVisibleToBuyer && (
        <div className="text-white">
          <p>Clean Price: $ {((prices?.cleanPriceCents || 0) / 100).toFixed(2)}</p>
          <p>Dirty Price: $ {((prices?.dirtyPriceCents || 0) / 100).toFixed(2)}</p>
        </div>
      )}

      {role === 'SELLER' && !prices?.isVisibleToBuyer && (
        <div className="flex flex-col gap-2">
          <div className="flex w-full items-center justify-items-start">
            <label className="block w-[120px] text-[12px] uppercase leading-[20px] text-white">
              Clean Price
            </label>
            <UiInput
              type="number"
              suffix="$"
              placeholder="0.00"
              value={cleanPrice}
              onChange={priceInputChangeHandler(setCleanPrice)}
              onBlur={persistWirePricesOnBlur}
              error={prices.cleanPriceCents === null}
              size={'small'}
              variant={'light'}
            />
          </div>
          <div className="flex w-full items-center">
            <label className="block w-[120px] text-[12px] uppercase leading-[20px] text-white">
              Dirty Price
            </label>
            <UiInput
              type="number"
              suffix="$"
              placeholder="0.00"
              value={dirtyPrice}
              onChange={priceInputChangeHandler(setDirtyPrice)}
              onBlur={persistWirePricesOnBlur}
              error={prices.dirtyPriceCents === null}
              size={'small'}
              variant={'light'}
            />
          </div>
          <Button
            label="prices"
            size="small"
            onClick={handleSendPrices}
            className="w-[150px]"
            disabled={!prices?.cleanPriceCents && !prices?.dirtyPriceCents}
            // disabled={!parseInt(cleanPrice) && !parseInt(dirtyPrice)}
          >
            Send Prices
          </Button>
        </div>
      )}
    </div>
  );
};
